import React, { useContext, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import WBCCheckbox from "../../elements/WBCCheckbox";
import WBCDataTable from "../../data-table/WBCDataTable";
import PropTypes from "prop-types";
import PlayerLinkFormatter from "../../table/PlayerLinkFormatter";
import PreferredNameFormatter from "../../table/PreferredNameFormatter";
import ChronicConditionsContext from "../../../contexts/ChronicConditionsContext";
import TableSortFilterContext from "../../../contexts/TableSortFilterContext";
import { sortPlayers } from "../../roster/RosterSortHelper";
import AuthContext from "../../../contexts/AuthContext";
import RosterStatusFormatter from "../mlb-player-summary/MLBPlayerRosterStatusFormatter";
import RequestStatusFormatter from "../../table/RequestStatusFormatter";
import useTournamentType from "../../hooks/useTournamentType";

const CheckboxContainer = styled.div`
  cursor: pointer;
`;

const ChronicConditionsTable = () => {
  // context variables
  const chronicConditionContext = useContext(ChronicConditionsContext);
  const tableSortFilterContext = useContext(TableSortFilterContext);
  const {
    filteredPlayerConditions,
    sortFilters,
    insuranceStatusSelected,
    setFilteredPlayerConditions,
    setSelectedChronicConditionIds,
    selectedChronicConditionIds,
    setInsuranceStatusSelected,
    insuranceActions,
    setSortFilters
  } = chronicConditionContext;
  const { isWBCIadmin } = useContext(AuthContext).state;
  const { isMainTournament: isMainTournamentEnabled } = useTournamentType();
  const [sortedRows, setSortedRows] = useState([]);

  const OrgClubFormatter = ({ value, row }) => {
    return row?.org?.toUpperCase() === "BOC" ? "" : value;
  };
  const ChronicConditionsSelectCheckbox = ({ ...props }) => {
    return isWBCIadmin ? (
      <CheckboxContainer>
        <WBCCheckbox checked={props.row.isChecked} isDisabled={props.row.disabled} />
      </CheckboxContainer>
    ) : null;
  };

  ChronicConditionsSelectCheckbox.propTypes = {
    row: PropTypes.any,
    isChecked: PropTypes.any,
    value: PropTypes.any,
    rowIdx: PropTypes.any,
    idx: PropTypes.any
  };

  useEffect(() => {
    const { col, dir } = sortFilters;
    setSortedRows(sortPlayers(col, dir, [...filteredPlayerConditions], false));
  }, [sortFilters, filteredPlayerConditions]);

  const onRowsSelected = row => {
    const newInsuranceStatus = insuranceActions.find(is => is.name === row?.insuranceStatus);
    if (insuranceStatusSelected === null && !!newInsuranceStatus) {
      setInsuranceStatusSelected({ ...newInsuranceStatus });
    }

    const updatedPlayerConditions = filteredPlayerConditions.map(player => {
      if (row.chronicConditionId === player.chronicConditionId) {
        player.isChecked = true;
      }
      if (player.insuranceStatus !== newInsuranceStatus?.name) {
        player.disabled = true;
      }
      return player;
    });
    setFilteredPlayerConditions([...updatedPlayerConditions]);
    setSelectedChronicConditionIds([
      ...updatedPlayerConditions.filter(i => i.isChecked).map(i => i.chronicConditionId)
    ]);
  };

  const onRowsDeselected = row => {
    const updatedPlayerConditions = filteredPlayerConditions.map(player => {
      if (row.chronicConditionId === player.chronicConditionId) {
        player.isChecked = false;
      }
      return player;
    });
    const newSelectedChronicConditions = updatedPlayerConditions
      .filter(i => i.isChecked)
      .map(i => i.chronicConditionId);
    setFilteredPlayerConditions([...updatedPlayerConditions]);
    setSelectedChronicConditionIds([...newSelectedChronicConditions]);
    if (newSelectedChronicConditions.length === 0) {
      setInsuranceStatusSelected(null);
      setFilteredPlayerConditions([...filteredPlayerConditions.map(player => ({ ...player, disabled: false }))]);
    }
  };

  const columns = [
    { name: "", key: "id", width: 50, formatter: ChronicConditionsSelectCheckbox },
    { name: "Insurance Status", key: "insuranceStatus", width: 180, sortable: true },
    { name: "Last Name", key: "lastName", width: 180, sortable: true, formatter: PlayerLinkFormatter },
    { name: "First Name", key: "firstName", width: 180, sortable: true, formatter: PlayerLinkFormatter },
    { name: "Preferred Name", key: "rosterFirstName", width: 180, sortable: true, formatter: PreferredNameFormatter },
    { name: "Pos", key: "position", sortable: true, width: 75 },
    { name: "Org", key: "org", width: 75, sortable: true, formatter: OrgClubFormatter },
    { name: "Club", key: "club", width: 180, sortable: true, formatter: OrgClubFormatter },
    { name: "Roster Status", key: "rosterStatus", sortable: true, formatter: RosterStatusFormatter, width: 180 },
    { name: "Request Status", key: "requestStatus", formatter: RequestStatusFormatter, sortable: true }
  ];

  const dynamicColumns = useMemo(() => {
    return isMainTournamentEnabled ? columns.filter(({ key }) => key !== "requestStatus") : columns;
  }, [columns, isMainTournamentEnabled]);

  return (
    <WBCDataTable
      useBCR
      columns={dynamicColumns}
      rowSelection={{
        showCheckbox: false,
        selectBy: {
          isSelectedKey: "isChecked"
        }
      }}
      onRowClick={(_idx, row, column) => {
        if (column?.idx === 0 && isWBCIadmin) {
          if (!row.disabled) {
            if (selectedChronicConditionIds?.includes(row?.chronicConditionId)) {
              onRowsDeselected(row);
            } else {
              onRowsSelected(row);
            }
          }
        }
      }}
      rowDisabledKey="disabled"
      headerRowHeight={40}
      minHeight={400}
      rowHeight={45}
      data={sortedRows}
      sortFunc={(col, dir) => {
        setSortFilters({ col, dir });
        tableSortFilterContext.dispatch({
          type: "setSortFilters",
          sortFilters: { col, dir }
        });
      }}
      sortColumn={sortFilters.col}
      sortDirection={sortFilters.dir}
    />
  );
};

export default ChronicConditionsTable;
